import React from 'react';
import clsx from 'clsx';
import { MagnifyingGlassMinusIcon, MagnifyingGlassPlusIcon } from '@heroicons/react/24/outline';
import {
  BrandSlotsState,
  BrandState,
  slotLogoKey,
  SlotPosition,
  CALLOUT_TINT,
  ELEMENTS,
  ElementControl,
  ElementStyle,
  elementOn,
  MarginSide,
  pageMarginIn,
  shows,
  slotLogoHeightPt,
  findElement,
  roleColor,
  fillSlot,
  partOn,
  readableOn,
  stripeTint,
  tintOver
} from './design-brand';
import { IconAction, LogoImage, TypefaceOption } from './design-fields';

/** The mark for a corner, when the corner asks for one and the brand has it; else null. */
const slotLogo = (
  brand: Pick<BrandState, 'logos' | 'header' | 'footer' | 'margins'>,
  part: 'header' | 'footer',
  key: SlotPosition
): React.ReactNode => {
  const slots = brand[part];
  const want = partOn(slots) ? slots?.[slotLogoKey(key)] : null;
  if (!want) return null;
  const mark = brand.logos.find((l) => l.variant === want.variant);
  if (!mark) return null;
  const marginIn = pageMarginIn(brand.margins, part === 'header' ? 'topIn' : 'bottomIn');
  return (
    <LogoImage
      mark={mark}
      alt="Agency logo"
      testId={`preview-logo-${key}`}
      height={slotLogoHeightPt(want.maxHeightPt, marginIn) * (96 / 72)}
    />
  );
};

/** One page fills this much of the window, so the first page lands whole on any screen. */
const PAGE_FRACTION_OF_WINDOW = 0.8;

/** The sheet at its design size in px: 44rem across, letter-proportioned. */
const SHEET_DESIGN_HEIGHT = (44 * 16 * 11) / 8.5;

/** The parts of the document a tab can point at. */
export type Region =
  | 'page'
  | 'body-page'
  | 'header'
  | 'body'
  | 'heading'
  | 'subheading'
  | 'table'
  | 'chart'
  | 'footer';

/** How much of the frame a region fills once it has been zoomed to. */
const REGION_FILL = 0.82;

/** How far the zoom may travel either side of the page that fits. */
const MIN_OF_FIT = 0.6;
const MAX_OF_FIT = 1.8;
const FOCUS_MS = 520;

/** Ease-out cubic: quick to leave, settling rather than stopping. */
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

/** The zoom that makes one page `fraction` of the window, whatever the window. */
const usePageZoom = (fraction: number, frame: React.RefObject<HTMLDivElement | null>) => {
  const measure = React.useCallback(() => {
    const available = frame.current?.clientWidth || window.innerWidth;
    return Math.min(
      1.25,
      Math.max(
        0.1,
        Math.min(
          (window.innerHeight * fraction) / SHEET_DESIGN_HEIGHT,
          Math.max(1, available - 64) / (44 * 16)
        )
      )
    );
  }, [fraction, frame]);
  const [fitted, setFitted] = React.useState(measure);
  const [zoom, setZoom] = React.useState(measure);
  const fit = React.useCallback(() => setZoom(measure()), [measure]);
  React.useLayoutEffect(() => {
    const onResize = () => {
      const next = measure();
      setFitted(next);
      setZoom(next);
    };
    onResize();
    window.addEventListener('resize', onResize);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(onResize);
    if (frame.current) observer?.observe(frame.current);
    return () => {
      window.removeEventListener('resize', onResize);
      observer?.disconnect();
    };
  }, [measure, frame]);
  return { zoom, setZoom, fitted, fit };
};
const useRegionFocus = (
  frame: React.RefObject<HTMLDivElement | null>,
  zoomRef: React.RefObject<number>,
  fittedRef: React.RefObject<number>,
  setZoom: (zoom: number) => void
) => {
  const animation = React.useRef<number | undefined>(undefined);

  React.useEffect(
    () => () => {
      if (animation.current) {
        cancelAnimationFrame(animation.current);
      }
    },
    []
  );

  return React.useCallback(
    (region: Region) => {
      const container = frame.current;
      const zoom = zoomRef.current;
      const target = container?.querySelector<HTMLElement>(`[data-region="${region}"]`);
      if (!container || !target || !zoom) {
        return;
      }

      // Rects come back already scaled by the zoom in force, so the region's own size is that
      // divided back out. Framing maths in zoomed pixels would chase its own tail.
      const rect = target.getBoundingClientRect();
      const frameRect = container.getBoundingClientRect();
      const naturalHeight = rect.height / zoom;
      const naturalWidth = rect.width / zoom;
      const naturalTop = (rect.top - frameRect.top + container.scrollTop) / zoom;

      // Framed on whichever axis runs out first. Height alone would zoom a 30px running header
      // to fill the frame, which is not a view of a header, it is a view of a line.
      const fits = Math.min(
        (container.clientWidth * REGION_FILL) / naturalWidth,
        (container.clientHeight * REGION_FILL) / naturalHeight
      );
      // A hidden preview, or one that is between layout passes, has zero-sized rects. Dividing
      // those dimensions yields NaN and an animation would permanently poison the zoom state.
      // Leave the fitted page alone until there is a measurable region to frame.
      if (!Number.isFinite(fits) || fits <= 0) {
        return;
      }
      // Never below the fitted page, and never more than twice it: a jump larger than that
      // reads as losing your place rather than moving closer.
      const fitted = fittedRef.current ?? 1;
      const toZoom = Math.min(fitted * MAX_OF_FIT, Math.max(fitted, fits));
      const fromZoom = zoom;
      const fromScroll = container.scrollTop;

      if (animation.current) {
        cancelAnimationFrame(animation.current);
      }

      const start = performance.now();
      const tick = (now: number) => {
        const t = easeOut(Math.min(1, (now - start) / FOCUS_MS));
        const at = fromZoom + (toZoom - fromZoom) * t;
        setZoom(at);

        // Recomputed each frame from the region's unzoomed position, so the scroll that centers
        // it stays right while the zoom underneath it is still moving.
        const centered = naturalTop * at - (container.clientHeight - naturalHeight * at) / 2;
        const wanted = Math.max(
          0,
          Math.min(centered, container.scrollHeight - container.clientHeight)
        );
        container.scrollTop = fromScroll + (wanted - fromScroll) * t;

        if (t < 1) {
          animation.current = requestAnimationFrame(tick);
        }
      };
      animation.current = requestAnimationFrame(tick);
    },
    [frame, zoomRef, fittedRef, setZoom]
  );
};

/** Pages the preview draws; what {pages} resolves to in a running slot. */
const PREVIEW_PAGES = 5;

const PreviewStyleContext = React.createContext<{
  brand: BrandState;
  sheet: React.CSSProperties;
  sheetPad: React.CSSProperties;
  heading: React.CSSProperties;
  styleFor: (element: ElementStyle['element'], fallbackFamily?: string) => React.CSSProperties;
} | null>(null);

// The renderer fills {page}, {pages} and {date} inside a slot, so the preview shows the same.
const running = (slots: BrandSlotsState | null, key: SlotPosition, page: number) =>
  partOn(slots) ? fillSlot(slots?.[key], page, PREVIEW_PAGES) : '';

/** A content page: running header, the page's own matter, running footer. */
const PreviewPage = ({
  number,
  region,
  children
}: {
  number: number;
  region?: Region;
  children: React.ReactNode;
}) => {
  const { brand, sheet, sheetPad, styleFor } = React.useContext(PreviewStyleContext)!;
  return (
    <div
      data-sheet
      data-region={region}
      className="flex flex-col overflow-hidden rounded-sm shadow-lg"
      style={{ ...sheet, ...sheetPad }}
    >
      <div data-region="header" className="mb-6 grid grid-cols-3 shrink-0 items-baseline">
        <span
          className="min-w-0 text-left font-semibold uppercase tracking-[0.18em]"
          style={{ fontSize: 10, ...styleFor(ELEMENTS.runningHeader) }}
        >
          {slotLogo(brand, 'header', 'left') ??
            (running(brand.header, 'left', number) || (
              <span
                aria-hidden
                data-testid="preview-empty-header-left"
                // dark-mode-exempt: the hint sits on the white sheet in both themes.
                className="inline-block h-3 w-12 border border-dashed border-neutral-300"
              />
            ))}
        </span>
        <span
          className="min-w-0 text-center"
          style={{ fontSize: 10, ...styleFor(ELEMENTS.runningHeader) }}
        >
          {slotLogo(brand, 'header', 'center') ?? running(brand.header, 'center', number)}
        </span>
        <span
          className="min-w-0 text-right"
          style={{ fontSize: 10, ...styleFor(ELEMENTS.runningHeader) }}
        >
          {slotLogo(brand, 'header', 'right') ?? running(brand.header, 'right', number)}
        </span>
      </div>

      <div className="min-h-0 flex-1">{children}</div>

      <div data-region="footer" className="mt-6 grid grid-cols-3 shrink-0 items-center">
        <span
          className="min-w-0 text-left opacity-50"
          style={{ fontSize: 10, ...styleFor(ELEMENTS.runningFooter) }}
        >
          {slotLogo(brand, 'footer', 'left') ??
            (running(brand.footer, 'left', number) || (
              <span
                aria-hidden
                data-testid="preview-empty-footer-left"
                // dark-mode-exempt: the hint sits on the white sheet in both themes.
                className="inline-block h-3 w-12 border border-dashed border-neutral-300"
              />
            ))}
        </span>
        <span
          className="min-w-0 text-center"
          style={{ fontSize: 10, ...styleFor(ELEMENTS.runningFooter) }}
        >
          {slotLogo(brand, 'footer', 'center') ?? running(brand.footer, 'center', number)}
        </span>
        <span
          className="min-w-0 text-right font-semibold"
          style={{ fontSize: 10, ...styleFor(ELEMENTS.runningFooter) }}
        >
          {slotLogo(brand, 'footer', 'right') ??
            (running(brand.footer, 'right', number) ||
              (partOn(brand.footer) ? `${number} of ${PREVIEW_PAGES}` : ''))}
        </span>
      </div>
    </div>
  );
};

const SectionHeading = ({ children }: { children: React.ReactNode }) => {
  const {
    heading,
    styleFor,
    brand: { palette, headingTypeface }
  } = React.useContext(PreviewStyleContext)!;
  return (
    <>
      <h3
        className="text-2xl"
        style={{ ...heading, ...styleFor(ELEMENTS.heading1, headingTypeface) }}
      >
        {children}
      </h3>
      <div
        className="mt-2 mb-4 h-px w-full"
        style={{ background: palette.primaryHex, opacity: 0.3 }}
      />
    </>
  );
};

const SubHeading = ({ children, region }: { children: React.ReactNode; region?: Region }) => {
  const {
    heading,
    styleFor,
    brand: { headingTypeface }
  } = React.useContext(PreviewStyleContext)!;
  return (
    <h4
      data-region={region}
      className="mt-8 text-lg"
      style={{ ...heading, ...styleFor(ELEMENTS.heading2, headingTypeface) }}
    >
      {children}
    </h4>
  );
};

export const DocumentPreview = ({
  brand,
  typefaces,
  region
}: {
  brand: BrandState;
  typefaces: TypefaceOption[];
  /** The part of the document being edited, which the preview zooms to when it changes. */
  region?: Region | null;
}) => {
  const frame = React.useRef<HTMLDivElement>(null);
  const { zoom, setZoom, fitted, fit } = usePageZoom(PAGE_FRACTION_OF_WINDOW, frame);
  const zoomRef = React.useRef(zoom);
  zoomRef.current = zoom;
  const fittedRef = React.useRef(fitted);
  fittedRef.current = fitted;

  const content = React.useRef<HTMLDivElement>(null);
  const bracket = React.useRef<HTMLDivElement>(null);

  const focus = useRegionFocus(frame, zoomRef, fittedRef, setZoom);

  /** Puts the bracket beside whatever is being edited. */
  React.useLayoutEffect(() => {
    const mark = bracket.current;
    const wrapper = content.current;
    if (!mark || !wrapper) {
      return;
    }
    // A bracket marks content within a sheet; whole-page targets have no content edge to mark.
    const target =
      region && region !== 'page' && region !== 'body-page'
        ? wrapper.querySelector<HTMLElement>(`[data-region="${region}"]`)
        : null;
    if (!target) {
      mark.style.opacity = '0';
      return;
    }

    const wrapperBox = wrapper.getBoundingClientRect();
    const box = target.getBoundingClientRect();
    const sheetBox = (target.closest('[data-sheet]') ?? target).getBoundingClientRect();

    mark.style.top = `${box.top - wrapperBox.top}px`;
    mark.style.height = `${box.height}px`;
    // Centered in the page's own margin, so it never sits on content and never needs a color
    // judged against the document's.
    mark.style.left = `${(sheetBox.left + box.left) / 2 - wrapperBox.left - 4}px`;
    mark.style.opacity = '1';
  });
  React.useEffect(() => {
    if (region) {
      focus(region);
    }
  }, [region, focus]);

  const step = (by: number) =>
    setZoom(Math.min(fitted * MAX_OF_FIT, Math.max(fitted * MIN_OF_FIT, zoom * by)));
  const { palette, bodyTypeface, headingTypeface } = brand;

  /** What one element's style comes to on the page. */
  const styleFor = (
    element: ElementStyle['element'],
    fallbackFamily?: string
  ): React.CSSProperties => {
    if (!elementOn(element, brand)) {
      return {};
    }
    const set = findElement(brand.elements, element);
    const on = (control: ElementControl) => shows(element, control);
    const color = on('color') ? roleColor(set.colorRole, palette) : null;
    const family = on('fontFamily') ? (set.fontFamily ?? fallbackFamily) : null;

    return {
      ...(family ? { fontFamily: stack(family) } : {}),
      ...(on('size') && set.sizePt ? { fontSize: `${(set.sizePt * 96) / 72}px` } : {}),
      ...(on('lineHeight') && set.lineHeight ? { lineHeight: set.lineHeight } : {}),
      ...(on('space') && set.spaceAbovePt != null
        ? { marginTop: `${(set.spaceAbovePt * 96) / 72}px` }
        : {}),
      ...(on('space') && set.spaceBelowPt != null
        ? { marginBottom: `${(set.spaceBelowPt * 96) / 72}px` }
        : {}),
      ...(color ? { color: color } : {}),
      ...(on('weight') && set.bold != null ? { fontWeight: set.bold ? 700 : 400 } : {}),
      ...(on('weight') && set.italic != null
        ? { fontStyle: set.italic ? 'italic' : 'normal' }
        : {}),
      // Widows and orphans are what these two actually are in CSS, and Chromium honors them
      // at print time, which is where the preview is trying to agree with the render.
      ...(on('linesKept') && set.linesKeptAtBottom ? { orphans: set.linesKeptAtBottom } : {}),
      ...(on('linesKept') && set.linesKeptAtTop ? { widows: set.linesKeptAtTop } : {}),
      ...(on('pageBreakBefore') && set.pageBreakBefore ? { breakBefore: 'page' } : {})
    };
  };
  const stack = (family: string) => typefaces.find((t) => t.family === family)?.cssStack ?? family;
  const headingStack = stack(headingTypeface);
  const onPrimary = readableOn(palette.primaryHex, palette.textHex);
  const heading = { fontFamily: headingStack, color: palette.primaryHex };
  const headerFill = roleColor(findElement(brand.elements, ELEMENTS.tableHeader).fillRole, palette);
  const headerRow = headerFill
    ? { background: headerFill, color: readableOn(headerFill, palette.textHex) }
    : undefined;
  // The renderer tints even rows from the primary until a stripe element speaks; one that
  // names no fill switches the tint off rather than falling back.
  const stripe = brand.elements.find((each) => each.element === ELEMENTS.tableStripe);
  const stripeBase = stripe ? roleColor(stripe.fillRole, palette) : palette.primaryHex;
  const rowTint = stripeBase ? stripeTint(stripeBase) : undefined;
  const callout = tintOver(palette.secondaryHex, palette.surfaceHex, CALLOUT_TINT);
  const sheet = {
    background: palette.surfaceHex,
    color: palette.textHex,
    fontFamily: stack(bodyTypeface),
    aspectRatio: '8.5 / 11'
  };

  // 1in is 96px, the same constant the type is converted at, so a 1in margin and 72pt of type
  // agree on the sheet.
  const marginPx = (side: MarginSide) => pageMarginIn(brand.margins, side) * 96;
  const sheetPad = {
    paddingTop: marginPx('topIn'),
    paddingRight: marginPx('rightIn'),
    paddingBottom: marginPx('bottomIn'),
    paddingLeft: marginPx('leftIn')
  };

  return (
    <PreviewStyleContext.Provider value={{ brand, sheet, sheetPad, heading, styleFor }}>
      <div className="relative rounded-lg bg-neutral-100 dark:bg-neutral-800">
        <div className="sticky top-4 z-10 flex justify-end px-4 pt-4 lg:px-8 lg:pt-8">
          <div className="flex items-center gap-0.5 rounded-lg border border-neutral-200 bg-white/90 p-0.5 shadow-sm backdrop-blur dark:border-neutral-700 dark:bg-neutral-800/90">
            <IconAction
              label="Zoom out"
              disabled={zoom <= fitted * MIN_OF_FIT + 0.001}
              onClick={() => step(1 / 1.25)}
            >
              <MagnifyingGlassMinusIcon className="size-4" />
            </IconAction>
            <button
              type="button"
              onClick={fit}
              title="Fit to the window"
              className="min-w-14 rounded-md px-1 py-1 text-xs font-medium tabular-nums text-neutral-700 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-white/10"
            >
              {Math.round((zoom / fitted) * 100)}%
            </button>
            <IconAction
              label="Zoom in"
              disabled={zoom >= fitted * MAX_OF_FIT - 0.001}
              onClick={() => step(1.25)}
            >
              <MagnifyingGlassPlusIcon className="size-4" />
            </IconAction>
          </div>
        </div>

        {/* A window onto the document rather than the whole of it: zooming to a region has to
          scroll something, and it must not be the settings page. */}
        <div
          ref={frame}
          data-preview-frame
          className="-mt-11 overflow-auto px-4 py-4 lg:px-8 lg:py-8"
          style={{ height: `calc(${PAGE_FRACTION_OF_WINDOW * 100}vh + 5rem)` }}
        >
          <div ref={content} className="relative">
            <div
              ref={bracket}
              aria-hidden
              data-testid="preview-bracket"
              className="pointer-events-none absolute w-2 transition-opacity duration-300"
              style={{ opacity: 0 }}
            >
              <div className="absolute inset-y-0 right-0 w-0.5 rounded-full bg-sky-500" />
              <div className="absolute right-0 top-0 h-0.5 w-2 rounded-full bg-sky-500" />
              <div className="absolute bottom-0 right-0 h-0.5 w-2 rounded-full bg-sky-500" />
            </div>

            {/* The frame scrolls rather than shrinking the sheet, so lines never reflow with zoom. */}
            <div className="mx-auto flex w-[44rem] max-w-none flex-col gap-8" style={{ zoom }}>
              <div
                data-region="page"
                data-sheet
                className="overflow-hidden rounded-sm shadow-lg"
                style={sheet}
              >
                <div
                  className="relative flex h-[62%] flex-col justify-end px-12 pb-10"
                  style={{ background: palette.primaryHex, color: onPrimary }}
                >
                  {/* A design prints no cover of its own; this one only shows the palette on a fill. */}
                  <span
                    // dark-mode-exempt: the chip sits on the sample's own fill in both themes.
                    className="absolute left-12 top-8 rounded bg-black/60 px-1.5 py-0.5 font-sans text-[10px] font-medium text-white"
                  >
                    Sample cover
                  </span>
                  <p className="text-[11px] uppercase tracking-[0.25em] opacity-70">Lorem ipsum</p>
                  <p
                    className="mt-3 text-4xl font-bold leading-tight"
                    style={{ fontFamily: headingStack }}
                  >
                    Dolor Sit Amet
                  </p>
                  <div className="mt-5 h-1 w-20" style={{ background: palette.accentHex }} />
                  <p className="mt-5 text-sm uppercase tracking-[0.18em] opacity-90">
                    Consectetur adipiscing elit sed
                  </p>
                </div>
                <div className="flex h-[38%] flex-col justify-between px-12 py-10">
                  <div>
                    <p className="text-xs uppercase tracking-widest opacity-60">Eiusmod tempor</p>
                    <p className="mt-1.5 text-xl font-bold" style={heading}>
                      Incididunt Ut Labore
                    </p>
                    <p className="mt-1 text-[13px] opacity-70">
                      Magna aliqua, ut enim ad minim veniam, quis nostrud exercitation
                    </p>
                  </div>
                  <p className="text-[11px] opacity-50">
                    Ullamco laboris nisi ut aliquip ex ea commodo consequat
                  </p>
                </div>
              </div>

              <PreviewPage number={2} region="body-page">
                <div data-region="heading">
                  <SectionHeading>Lorem Ipsum Dolor</SectionHeading>
                  <p
                    data-region="body"
                    className="text-[13px] leading-relaxed"
                    style={styleFor(ELEMENTS.body, bodyTypeface)}
                  >
                    Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor
                    incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis
                    nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat
                    duis aute irure.
                  </p>
                  <SubHeading region="subheading">Consectetur Adipiscing</SubHeading>
                </div>

                <div
                  className="my-6 border-l-4 p-5"
                  style={{ borderColor: palette.accentHex, background: callout }}
                >
                  <p className="text-[11px] uppercase tracking-widest opacity-60">
                    Consectetur adipiscing
                  </p>
                  <p className="mt-1 text-3xl font-bold" style={{ color: palette.secondaryHex }}>
                    $1,234,567
                  </p>
                  <p className="mt-1.5 text-[13px] opacity-80">
                    Sed do <span className="font-semibold">eiusmod tempor</span> incididunt ut
                    labore et dolore magna aliqua.
                  </p>
                </div>

                <p className="text-[13px] leading-relaxed">
                  Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu
                  fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in
                  culpa qui officia deserunt mollit anim id est laborum.
                </p>
              </PreviewPage>

              <PreviewPage number={3}>
                <SectionHeading>Ut Enim Ad Minim</SectionHeading>
                <table data-region="table" className="w-full border-collapse text-[12px]">
                  <thead>
                    <tr data-region="table-header" style={headerRow}>
                      {['#', 'Lorem ipsum dolor', 'Sit amet', 'Consectetur'].map((h) => (
                        <th
                          key={h}
                          className="px-3 py-2 text-left font-semibold"
                          style={styleFor(ELEMENTS.tableHeader)}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['1', 'Consectetur adipiscing elit', 'Tempor', '$4,250,000'],
                      ['2', 'Sed do eiusmod incididunt', 'Labore', '$3,100,000'],
                      ['3', 'Ut labore et dolore magna', 'Aliqua', '$3,800,000'],
                      ['4', 'Quis nostrud exercitation', 'Ullamco', '$1,650,000'],
                      ['5', 'Nisi ut aliquip ex ea', 'Commodo', '$2,975,000'],
                      ['6', 'Duis aute irure dolor', 'Velit', '$5,410,000'],
                      ['7', 'Excepteur sint occaecat', 'Proident', '$2,180,000'],
                      ['8', 'Cupidatat non sunt culpa', 'Officia', '$4,640,000']
                    ].map((row, i) => (
                      <tr key={row[0]} style={{ background: i % 2 ? rowTint : undefined }}>
                        {row.map((cell, j) => (
                          <td
                            key={cell}
                            className={clsx('px-3 py-2', j === 3 && 'text-right font-medium')}
                            style={{
                              borderBottom: `1px solid ${palette.textHex}1a`,
                              ...styleFor(ELEMENTS.tableCell)
                            }}
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>

                <SubHeading>Quis Nostrud Exercitation</SubHeading>
                <dl className="mt-3 grid grid-cols-2 gap-x-8">
                  {[
                    ['Lorem ipsum dolor', '$34,800,000'],
                    ['Consectetur adipiscing', '$6,200,000 · 12 mensis'],
                    ['Sed do eiusmod tempor', '3% incididunt ut labore'],
                    ['Magna aliqua ut enim', '$50,000 per veniam'],
                    ['Nostrud exercitation', 'Ullamco'],
                    ['Aliquip ex ea commodo', '$2,000,000 consequat']
                  ].map(([term, value]) => (
                    <div
                      key={term}
                      className="flex justify-between gap-4 py-2 text-[12px]"
                      style={{ borderBottom: `1px solid ${palette.textHex}14` }}
                    >
                      <dt className="opacity-70">{term}</dt>
                      <dd className="text-right font-medium">{value}</dd>
                    </div>
                  ))}
                </dl>
              </PreviewPage>

              <PreviewPage number={4}>
                <SectionHeading>Sed Do Eiusmod</SectionHeading>
                <div data-region="chart" className="flex items-end gap-2" style={{ height: 150 }}>
                  {[
                    ['Lorem', 0.52, palette.primaryHex],
                    ['Ipsum', 0.78, palette.primaryHex],
                    ['Dolor', 0.41, palette.secondaryHex],
                    ['Sit', 0.93, palette.primaryHex],
                    ['Amet', 0.66, palette.secondaryHex],
                    ['Elit', 0.34, palette.accentHex],
                    ['Tempor', 0.71, palette.primaryHex],
                    ['Labore', 0.58, palette.secondaryHex]
                  ].map(([label, value, fill]) => (
                    <div key={label as string} className="flex flex-1 flex-col items-center gap-1">
                      <div
                        className="w-full rounded-t-sm"
                        style={{
                          height: `${(value as number) * 120}px`,
                          background: fill as string
                        }}
                      />
                      <span
                        className="opacity-60"
                        style={{ fontSize: 9, ...styleFor(ELEMENTS.chartLabel) }}
                      >
                        {label as string}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="h-px w-full" style={{ background: `${palette.textHex}22` }} />

                <div className="mt-6 flex gap-4">
                  {[
                    ['Lorem ipsum', '42%', palette.primaryHex],
                    ['Dolor sit', '31%', palette.secondaryHex],
                    ['Amet elit', '27%', palette.accentHex]
                  ].map(([label, value, fill]) => (
                    <div
                      key={label as string}
                      className="flex-1 border-l-4 py-1 pl-3"
                      style={{ borderColor: fill as string }}
                    >
                      <p className="text-2xl font-bold" style={{ color: palette.primaryHex }}>
                        {value as string}
                      </p>
                      <p className="text-[11px] opacity-60">{label as string}</p>
                    </div>
                  ))}
                </div>
              </PreviewPage>

              <PreviewPage number={5}>
                <SectionHeading>Duis Aute Irure</SectionHeading>
                <p className="text-[13px] leading-relaxed">
                  Dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla
                  pariatur, excepteur sint occaecat cupidatat non proident.
                </p>
                <ul className="mt-3 flex flex-col gap-2 text-[13px] leading-relaxed">
                  {[
                    'Sunt in culpa qui officia deserunt mollit anim id est laborum.',
                    'Sed ut perspiciatis unde omnis iste natus error sit voluptatem.',
                    'Accusantium doloremque laudantium, totam rem aperiam eaque ipsa.'
                  ].map((item) => (
                    <li key={item} className="flex gap-2.5">
                      <span
                        className="mt-1.5 size-1.5 shrink-0 rounded-full"
                        style={{ background: palette.accentHex }}
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <SubHeading>Excepteur Sint Occaecat</SubHeading>
                <ol className="mt-3 flex flex-col gap-3 text-[13px] leading-relaxed">
                  {[
                    'Quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt.',
                    'Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit.',
                    'Neque porro quisquam est qui dolorem ipsum quia dolor sit amet consectetur.'
                  ].map((item, i) => (
                    <li key={item} className="flex gap-3">
                      <span
                        className="flex size-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold"
                        style={{ background: palette.primaryHex, color: onPrimary }}
                      >
                        {i + 1}
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ol>

                <div className="mt-10 flex gap-10">
                  {['Lorem ipsum', 'Dolor sit amet'].map((who) => (
                    <div key={who} className="flex-1">
                      <div className="h-12 border-b" style={{ borderColor: palette.textHex }} />
                      <p className="mt-1.5 text-[11px] opacity-60">{who}</p>
                    </div>
                  ))}
                </div>
              </PreviewPage>
            </div>
          </div>
        </div>
      </div>
    </PreviewStyleContext.Provider>
  );
};
